<script setup>
const emit = defineEmits(['refresh', 'interactionCreated'])
const props = defineProps({
  targetType: {
    type: String,
    required: true,
  },
  targetId: {
    type: Number,
    required: true,
  },
  initiatorVacancyId: {
    type: Number,
    default: null,
  },
  companyShowSlideover: {
    type: Boolean,
    default: true,
  },
  canRetract: {
    type: Boolean,
    default: true,
  },
  size: {
    type: String,
    default: 'sm',
  },
  text: {
    type: String,
    default: '',
  },
})

const user = useStrapiUser()
const slideOverStore = useSlideOverStore()
const apiInteraction = useApiInteraction()
const { fetchUser } = useStrapiAuth()
const { confirm } = useConfirm()

let working = false

const interactionId = computed(() => {
  // Never show current interaction to company to be able to show interest as vacancies
  if (user.value?.type === 'company' && props.companyShowSlideover) return null

  if (props.initiatorVacancyId) {
    const interaction = user.value?.company?.vacancies
      ?.find((vacancy) => vacancy.id === props.initiatorVacancyId)
      ?.interactions?.find(
        (i) =>
          i?.[props.targetType]?.id === props.targetId &&
          i.initiator ===
            (props.initiatorVacancyId ? 'vacancy' : user.value.type),
      )
    return interaction?.id
  } else {
    const interaction = user.value?.[user.value.type]?.interactions?.find(
      (i) =>
        i?.[props.targetType]?.id === props.targetId &&
        i.initiator ===
          (props.initiatorVacancyId ? 'vacancy' : user.value.type),
    )
    return interaction?.id
  }
})

const isProfessional = computed(() => user.value?.type === 'professional')

const professionalHasNoFinishedAssessments = computed(() =>
  user.value?.professional?.candidate?.assessments.every((assessment) =>
    assessment?.tasks.every((task) => task.finished === false),
  ),
)

async function createInterest() {
  if (working) return
  working = true
  if (user.value.type === 'company' && props.companyShowSlideover) {
    slideOverStore.openSlideover('CompaniesShowInterest', {
      targetId: props.targetId,
      callback: () => refresh(),
    })
  } else {
    const createInteraction = async () => {
      await apiInteraction.createInteraction({
        type: 'interested',
        targetId: props.targetId,
        targetType: props.targetType,
        ...(props.initiatorVacancyId
          ? {
              initiatorType: 'vacancy',
              initiatorId: props.initiatorVacancyId,
            }
          : {}),
      })

      emit('interactionCreated')
      refresh()
    }

    let confirmed = true
    if (professionalHasNoFinishedAssessments.value) {
      confirmed = await confirm({
        title: 'Je toont interesse zonder dat je jouw assessments hebt gemaakt',
        description:
          'Maak eerst je assessments zodat je gelijk een goede indruk geeft aan het bedrijf',
        confirmText: 'Toch anoniem interesse tonen',
        cancelText: 'Interesse annuleren',
      })
    } else if (user.value.type === 'professional') {
      confirmed = await confirm({
        title: 'Weet je zeker dat je anoniem interesse wilt tonen?',
        description: '',
        confirmText: 'Anoniem interesse tonen',
        cancelText: 'Interesse annuleren',
      })
    }

    if (confirmed) {
      await createInteraction()
    }
  }
  refresh()
  working = false
}

async function deleteInterest() {
  if (working) return
  working = true
  await apiInteraction.deleteInteraction(interactionId.value)
  refresh()
  working = false
}

function refresh() {
  emit('refresh')
  fetchUser()
}
const textWithDefault = computed(() => {
  if (props.text) return props.text

  return user.value.type === 'professional'
    ? 'Anoniem interesse tonen'
    : 'interesse tonen'
})
</script>

<template>
  <Button
    v-if="!interactionId || !canRetract"
    theme="accent"
    :text="textWithDefault"
    :size="size"
    :info="
      isProfessional
        ? 'De ander wordt op de hoogte gesteld van jouw interesse zonder dat jouw identiteit bekend wordt gemaakt.'
        : 'De professional wordt op de hoogte gebracht van jouw interesse en wordt verzocht om te reageren.'
    "
    @click="createInterest"
  />

  <Button
    v-else
    theme="secondary"
    text="interesse terugtrekken"
    :size="size"
    @click="deleteInterest"
  />
</template>
